import { logger } from '../utils/logger';
import { makeAuthenticatedRequest } from '../utils/api';
import { getPlayerStats, PlayerStats } from '../utils/achievements';
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import VersionInfo from './VersionInfo';
import Characters from './Characters';
import HexWorldMap from './HexWorldMap';
import BattleGrid from './BattleGrid';
import Crafting from './Crafting';
import Achievements from './Achievements';
import Leaderboards from './Leaderboards';
import AdminDashboard from './AdminDashboard';
import HelpOverlay from './HelpOverlay';
import ErrorBoundary from './ErrorBoundary';
import { useConfirm } from './ConfirmModal';
import TutorialLibrary from './TutorialLibrary';
import QuestProgressPanel from './QuestProgressPanel';
import DailyQuestPanel from './DailyQuestPanel';
import WeeklyChallengePanel from './WeeklyChallengePanel';
import UserSettings from './UserSettings';

interface HomeDashboardProps {
  username: string | undefined;
  showWelcome: boolean;
  isCreatingStarter: boolean;
  playerStats: PlayerStats | null;
  onNavigate: (path: string) => void;
}

const HomeDashboard: React.FC<HomeDashboardProps> = ({ username, showWelcome, isCreatingStarter, playerStats, onNavigate }) => (
  <div className="home-dashboard">
    {showWelcome && (
      <div className="welcome-banner">
        <h2>Welcome to Heroes of the Grid!</h2>
        <p>We've created your first hero to get you started. Visit the <strong>Characters</strong> page to view your hero, then explore the <strong>World Map</strong> to begin your adventure!</p>
      </div>
    )}
    {isCreatingStarter && (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>Creating your first hero...</p>
      </div>
    )}
    <div className="welcome-section">
      <h2>{showWelcome ? `Welcome, ${username}!` : `Welcome back, ${username}!`}</h2>
      <p>Your turn-based tactical adventure awaits. Choose your path:</p>

      <div className="quick-actions">
        <div className="action-card action-card-characters" onClick={() => onNavigate('/characters')}>
          <div className="card-icon">⚔️</div>
          <h3>Characters</h3>
          <p>Manage your heroes and unlock their potential</p>
          <div className="card-hint">Click to view →</div>
        </div>
        <div className="action-card action-card-hexworld" onClick={() => onNavigate('/world')}>
          <div className="card-icon">🗺️</div>
          <h3>World Map</h3>
          <p>Explore a vast procedural world with resources and battles</p>
          <div className="card-hint">Explore world →</div>
        </div>
        <div className="action-card action-card-crafting" onClick={() => onNavigate('/crafting')}>
          <div className="card-icon">🔨</div>
          <h3>Crafting</h3>
          <p>Forge powerful equipment from gathered materials</p>
          <div className="card-hint">Create items →</div>
        </div>
        <div className="action-card action-card-achievements" onClick={() => onNavigate('/achievements')}>
          <div className="card-icon">🏆</div>
          <h3>Achievements</h3>
          <p>Track your progress and unlock rewards</p>
          <div className="card-hint">View achievements →</div>
        </div>
        <div className="action-card action-card-leaderboards" onClick={() => onNavigate('/leaderboards')}>
          <div className="card-icon">👑</div>
          <h3>Leaderboards</h3>
          <p>See how you stack up against other players</p>
          <div className="card-hint">View rankings →</div>
        </div>
      </div>

      <DailyQuestPanel />
      <WeeklyChallengePanel />
      <QuestProgressPanel />
    </div>

    {playerStats && (
      <div className="recent-activity">
        <h3>Your Adventure</h3>
        <div className="activity-list">
          <div className="activity-item">
            <span className="activity-icon">⚔️</span>
            <div className="activity-text">
              <p>{playerStats.battlesWon} {playerStats.battlesWon === 1 ? 'battle' : 'battles'} won</p>
              <small>{playerStats.monstersDefeated} monsters defeated · {playerStats.totalDamageDealt.toLocaleString()} total damage dealt</small>
            </div>
          </div>
          <div className="activity-item">
            <span className="activity-icon">🔨</span>
            <div className="activity-text">
              <p>{playerStats.itemsCrafted} {playerStats.itemsCrafted === 1 ? 'item' : 'items'} crafted</p>
              <small>{playerStats.recipesDiscovered} {playerStats.recipesDiscovered === 1 ? 'recipe' : 'recipes'} discovered</small>
            </div>
          </div>
          <div className="activity-item">
            <span className="activity-icon">🗺️</span>
            <div className="activity-text">
              <p>{playerStats.tilesExplored} {playerStats.tilesExplored === 1 ? 'tile' : 'tiles'} explored</p>
              <small>{playerStats.materialsGathered} materials gathered</small>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

interface BattleRouteProps {
  onExit: () => void;
}

const BattleRoute: React.FC<BattleRouteProps> = ({ onExit }) => {
  const { battleId } = useParams<{ battleId: string }>();
  const numericBattleId = Number(battleId);

  if (!battleId || Number.isNaN(numericBattleId)) {
    return (
      <div className="battle-error">
        <p>No battle ID available. Please return to world map and start a new battle.</p>
        <button onClick={onExit}>Return to World Map</button>
      </div>
    );
  }

  return (
    <ErrorBoundary
      title="The battle hit an unexpected error"
      resetLabel="Return to World Map"
      onReset={onExit}
    >
      <BattleGrid battleId={numericBattleId} onExitBattle={onExit} />
    </ErrorBoundary>
  );
};

const Home: React.FC = () => {
  const { user, logout } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreatingStarter, setIsCreatingStarter] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTutorialLibrary, setShowTutorialLibrary] = useState(false);
  const [activeBattle, setActiveBattle] = useState<{ id: number; name: string } | null>(null);
  const [isCheckingBattle, setIsCheckingBattle] = useState(true);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const { confirm, confirmDialog } = useConfirm();
  const lastLockedNavToastRef = useRef(0);
  const prevPathRef = useRef(location.pathname);

  const onBattleRoute = location.pathname.startsWith('/battle/');
  const navLocked = onBattleRoute && !!activeBattle;
  const lockedCls = navLocked ? ' nav-locked' : '';

  // Close the "More" sheet on any screen change - some transitions (battle start/end,
  // the ErrorBoundary reset) navigate directly rather than through handleNavigation,
  // so clearing it only in handleNavigation would leave it stranded on top of the new screen.
  useEffect(() => {
    setShowMoreMenu(false);
  }, [location.pathname]);

  const notifyBattleLocked = () => {
    const now = Date.now();
    if (now - lastLockedNavToastRef.current > 2000) {
      lastLockedNavToastRef.current = now;
      addNotification({
        type: 'info',
        title: 'Battle in Progress',
        message: 'Finish or flee the battle first.',
      });
    }
  };

  // Hard-block navigation while a battle is in progress
  const handleNavigation = (path: string) => {
    if (navLocked) {
      notifyBattleLocked();
      return;
    }
    navigate(path);
  };

  const handleExitBattle = () => {
    setActiveBattle(null);
    navigate('/world');
    // GH-86: react-router wraps navigation in startTransition, and a wedged React
    // transition lane (seen on React 19.1.0) can leave the URL changed but the battle
    // screen mounted forever - trapping the player on the victory screen. If the
    // battle UI is still in the DOM well after the navigate, escape via a full page
    // load (requires the GH-87 SPA rewrite so /world doesn't 404).
    setTimeout(() => {
      if (document.querySelector('.battle-screen')) {
        window.location.href = '/world';
      }
    }, 800);
  };

  // Helper function to handle logout with battle warning
  const handleLogout = async () => {
    // If currently in battle, show warning
    if (navLocked) {
      const confirmed = await confirm({
        title: 'Abandon Battle?',
        message: "You're currently in battle! Logging out will abandon the fight.\n\nAre you sure you want to logout?",
        confirmText: 'Logout',
        danger: true,
      });
      if (!confirmed) {
        return; // Don't logout
      }
    }
    logout();
  };

  useEffect(() => {
    const checkAndCreateStarterCharacter = async () => {
      if (!user) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // Check if user has any characters
        const response = await makeAuthenticatedRequest('/characters');

        if (response.ok) {
          const characters = await response.json();

          // If no characters, create a starter character
          if (characters.length === 0) {
            setIsCreatingStarter(true);

            const createResponse = await makeAuthenticatedRequest('/characters/create-starter', {
              method: 'POST'
            });

            if (createResponse.ok) {
              setShowWelcome(true);
              setTimeout(() => setShowWelcome(false), 32000);

              // Show notification toast
              addNotification({
                type: 'info',
                title: 'Welcome to Heroes of the Grid!',
                message: 'Your first hero has been created. Begin your adventure!'
              });
            }

            setIsCreatingStarter(false);
          }
        }
      } catch (error) {
        logger.error('Error checking/creating starter character:', error);
        setIsCreatingStarter(false);
      }
    };

    checkAndCreateStarterCharacter();
  }, [user]);

  useEffect(() => {
    const checkActiveBattle = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsCheckingBattle(false);
        return;
      }
      try {
        const res = await makeAuthenticatedRequest('/battles/active');
        if (res.ok) {
          const data = await res.json();
          const battle = data.activeBattle ?? null;
          setActiveBattle(battle);
          if (battle) {
            const battlePath = `/battle/${battle.id}`;
            if (location.pathname !== battlePath) {
              navigate(battlePath, { replace: true });
            }
          }
        }
      } catch { /* non-critical */ }
      finally {
        setIsCheckingBattle(false);
      }
    };
    checkActiveBattle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Route guard: if the player tries to leave the battle screen while a battle is
  // still in progress (e.g. via the browser's back button, which bypasses
  // handleNavigation entirely), snap them back and surface the same toast used
  // for blocked nav-menu clicks.
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const wasLocked = prevPath.startsWith('/battle/') && !!activeBattle;
    const nowOnBattle = location.pathname.startsWith('/battle/');
    if (wasLocked && !nowOnBattle) {
      notifyBattleLocked();
      navigate(`/battle/${activeBattle!.id}`, { replace: true });
    }
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, activeBattle]);

  // Load player stats for the home dashboard whenever the home screen is shown
  useEffect(() => {
    if (!user || location.pathname !== '/') return;
    getPlayerStats().then(setPlayerStats).catch(err => {
      logger.error('Error loading player stats:', err);
    });
  }, [user, location.pathname]);

  // Fire the UI orientation tutorial on first Home visit, before any of the more
  // specific screen tutorials (battle/world-map/crafting/etc.) get a chance to -
  // this is the very first screen shown, so gating on it firing here beats every
  // other checkAndStartTutorial call, which only run once their own screen mounts.
  useEffect(() => {
    if (!user || isCheckingBattle || location.pathname !== '/') return;
    checkAndStartTutorial('ui-basics');
  }, [user, isCheckingBattle, location.pathname, checkAndStartTutorial]);

  if (isCheckingBattle) {
    return (
      <div className="home">
        <div className="battle-lock-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <div className="home">
      {confirmDialog}
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {showTutorialLibrary && <TutorialLibrary onClose={() => setShowTutorialLibrary(false)} />}
      <header className="home-header">
        <h1>Heroes of the Grid</h1>
        <div className="user-info">
          <button onClick={() => setShowTutorialLibrary(true)} className="tutorial-test-button" title="Tutorial Library">
            📚
          </button>
          <button onClick={() => setShowHelp(true)} className="help-button" title="How to Play">
            ?
          </button>
          <span>Welcome, {user?.username}!</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <nav className="main-nav">
        <button
          className={`nav-button ${isActive('/') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/')}
        >
          Home
        </button>
        <button
          className={`nav-button ${isActive('/characters') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/characters')}
        >
          Characters
        </button>
        <button
          className={`nav-button ${isActive('/world') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/world')}
        >
          World Map
        </button>
        <button
          className={`nav-button ${isActive('/crafting') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/crafting')}
        >
          Crafting
        </button>
        <button
          className={`nav-button ${isActive('/achievements') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/achievements')}
        >
          Achievements
        </button>
        <button
          className={`nav-button ${isActive('/leaderboards') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/leaderboards')}
        >
          Leaderboards
        </button>
        <button
          className={`nav-button ${isActive('/settings') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/settings')}
        >
          Settings
        </button>
        {user?.role === 'Admin' && (
          <button
            className={`nav-button ${isActive('/admin') ? 'active' : ''}${lockedCls}`}
            onClick={() => handleNavigation('/admin')}
          >
            Admin Panel
          </button>
        )}
      </nav>

      {showMoreMenu && (
        <div className="more-menu-backdrop" onClick={() => setShowMoreMenu(false)}>
          <div className="more-menu-sheet" onClick={(e) => e.stopPropagation()}>
            <button
              className={`more-menu-item ${isActive('/achievements') ? 'active' : ''}${lockedCls}`}
              onClick={() => handleNavigation('/achievements')}
            >
              🏆 Achievements
            </button>
            <button
              className={`more-menu-item ${isActive('/leaderboards') ? 'active' : ''}${lockedCls}`}
              onClick={() => handleNavigation('/leaderboards')}
            >
              👑 Leaderboards
            </button>
            <button
              className={`more-menu-item ${isActive('/settings') ? 'active' : ''}${lockedCls}`}
              onClick={() => handleNavigation('/settings')}
            >
              ⚙️ Settings
            </button>
            {user?.role === 'Admin' && (
              <button
                className={`more-menu-item ${isActive('/admin') ? 'active' : ''}${lockedCls}`}
                onClick={() => handleNavigation('/admin')}
              >
                🛡️ Admin Panel
              </button>
            )}
            <button className="more-menu-item more-menu-logout" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      )}

      <nav className="bottom-tab-bar">
        <button
          className={`bottom-tab-btn ${isActive('/') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/')}
        >
          <span className="bottom-tab-icon">🏠</span>
          <span className="bottom-tab-label">Home</span>
        </button>
        <button
          className={`bottom-tab-btn ${isActive('/characters') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/characters')}
        >
          <span className="bottom-tab-icon">⚔️</span>
          <span className="bottom-tab-label">Heroes</span>
        </button>
        <button
          className={`bottom-tab-btn ${isActive('/world') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/world')}
        >
          <span className="bottom-tab-icon">🗺️</span>
          <span className="bottom-tab-label">World</span>
        </button>
        <button
          className={`bottom-tab-btn ${isActive('/crafting') ? 'active' : ''}${lockedCls}`}
          onClick={() => handleNavigation('/crafting')}
        >
          <span className="bottom-tab-icon">🔨</span>
          <span className="bottom-tab-label">Craft</span>
        </button>
        <button
          className={`bottom-tab-btn ${showMoreMenu ? 'active' : ''}`}
          onClick={() => setShowMoreMenu((v) => !v)}
        >
          <span className="bottom-tab-icon">☰</span>
          <span className="bottom-tab-label">More</span>
        </button>
      </nav>

      <main className="main-content">
        <Routes>
          <Route
            index
            element={
              <HomeDashboard
                username={user?.username}
                showWelcome={showWelcome}
                isCreatingStarter={isCreatingStarter}
                playerStats={playerStats}
                onNavigate={handleNavigation}
              />
            }
          />
          <Route path="characters/*" element={<Characters />} />
          <Route
            path="world"
            element={<HexWorldMap onStartBattle={(battleId) => navigate(`/battle/${battleId}`)} />}
          />
          <Route path="crafting" element={<Crafting />} />
          <Route path="achievements" element={<Achievements />} />
          <Route path="leaderboards" element={<Leaderboards />} />
          <Route path="settings" element={<UserSettings />} />
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="battle/:battleId" element={<BattleRoute onExit={handleExitBattle} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="version-footer">
        <VersionInfo />
      </footer>
    </div>
  );
};

export default Home;
